import { createApolloClient, InMemoryCache } from '@fs/zion-graphql'

/**
 * Cache for the FamilySearch graph.
 *
 * Normalization is disabled for a few types whose `id` is NOT a reliable global identity.
 * InMemoryCache keys entities on `__typename` + `id` across the whole cache, so two distinct
 * objects that happen to share an id collapse into one and the last write wins. Storing these
 * inline in their parent avoids that.
 *
 * - Conclusion: ids are *usually* unique, which is what makes this dangerous — it works until
 *   it doesn't. They look like UUIDs, so it is reasonable to assume they are globally unique.
 *   They are not. Verified at the source: `GET tf.tree.service /tf/person/{id}` returns
 *   name.conclusionId 991a0a6e-291e-4f21-a0db-159dd32b100f for BOTH KWCY-WRD (John Henry Graf)
 *   and KWC1-867 (Marion Jacob Graf), while their commandUuid and valueHash differ — so these
 *   are two distinct conclusions sharing one id, straight from TF, not a subgraph mapping bug.
 *   TF's sibling `ctConclusionId` is not populated on that endpoint, so there is no
 *   better-behaved id to key on. With normalization on, the merge page rendered "John Henry
 *   Graf" on both cards while keeping the duplicate's own lifespan.
 * - Contributor: keyed by CIS user id, but each instance also carries the modification date of
 *   the conclusion it hangs off. One prod merge analysis returned 17 contributor ids of which
 *   10 collided with differing dateMs, so normalizing would smear one timestamp across all of
 *   that user's contributions.
 * - PlaceInfo: `id` is the shared place-authority id, while `originalText`/`normalizedText` are
 *   conclusion-scoped — the same place referenced by two conclusions is two different objects.
 *
 * MergeSourceValue is deliberately left normalized: its id is a per-person source id and no
 * collisions were observed across 32 of them in the same payload.
 */
function createTreePersonCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      Conclusion: { keyFields: false },
      Contributor: { keyFields: false },
      PlaceInfo: { keyFields: false },
    },
  })
}

/**
 * The app-wide Apollo client for the FamilySearch federation router (/service/nebo).
 * Extra options are for test clients (e.g. Cypress disables reload-on-401 and Sentry reporting).
 */
export function createTreePersonApolloClient(
  options?: Omit<Parameters<typeof createApolloClient>[0], 'cache'>
): ReturnType<typeof createApolloClient> {
  return createApolloClient({ cache: createTreePersonCache(), clientName: 'tree-person-r9', ...options })
}
